import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, Mail, Heart, ArrowLeft, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { LivePresenceIndicator } from "./LivePresenceIndicator";
import { PhoneSignUp } from "./PhoneSignUp";
import { EmailSignUp } from "./EmailSignUp";
import { OTPVerification } from "./OTPVerification";
import { PhoneInput } from "./PhoneInput";
import { PasswordReset } from "./PasswordReset";
import { useGeoData, GeoCountry } from "@/hooks/useGeoData";
import { getPhoneCode } from "@/lib/countryPhoneCodes";
import { parsePhoneNumber } from "libphonenumber-js";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { FEATURE_FLAGS } from "@/lib/featureFlags";

// Google Icon Component
const GoogleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

interface AuthMethodSelectorProps {
  onPhoneClick?: () => void;
  onEmailClick?: () => void;
}

export const AuthMethodSelector = ({
  onPhoneClick,
  onEmailClick,
}: AuthMethodSelectorProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signIn, signInWithGoogle } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [signUpMethod, setSignUpMethod] = useState<"email" | "phone" | null>(null);
  const [signInMethod, setSignInMethod] = useState<"email" | "phone">("email"); // Default to email
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string; phone?: string }>({});
  
  // Phone sign-in states
  const [selectedCountry, setSelectedCountry] = useState<GeoCountry | null>(null);
  const [countryCode, setCountryCode] = useState("+1");
  const [showPhoneOTP, setShowPhoneOTP] = useState(false);

  // Get last used method from localStorage
  useEffect(() => {
    const lastMethod = localStorage.getItem("lastSignInMethod");
    if (lastMethod === "email" || lastMethod === "phone") {
      setSignInMethod(lastMethod);
    }
  }, []);

  // Load default country for phone sign-in
  useEffect(() => {
    if (signInMethod === "phone" && !selectedCountry) {
      const loadDefaultCountry = async () => {
        const { getCountries } = useGeoData();
        const allCountries = await getCountries();
        // Default to US
        const usCountry = allCountries.find((c) => c.iso2 === "US");
        if (usCountry) {
          setSelectedCountry(usCountry);
          setCountryCode(getPhoneCode(usCountry.iso2));
        }
      };
      loadDefaultCountry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signInMethod]);

  const handleGoogleSignIn = async () => {
    if (isGoogleLoading) return; // Prevent double-click
    
    setIsGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        console.error("Google sign-in error:", error);
        toast.error(error.message || t("entry.failedToSignInWithGoogle"));
        setIsGoogleLoading(false);
      } else {
        // OAuth redirect will happen, so we don't need to handle navigation here
        // The auth state change will handle redirect to onboarding/dashboard
        toast.success(t("entry.redirectingToGoogle") || "Redirecting to Google...");
      }
    } catch (err: any) {
      console.error("Google sign-in error:", err);
      toast.error(err.message || t("entry.failedToSignInWithGoogle"));
      setIsGoogleLoading(false);
    }
  };

  const handlePhoneClick = async () => {
    if (onPhoneClick) {
      onPhoneClick();
      return;
    }

    setShowSignUp(true);
    setSignUpMethod("phone");
  };

  const handleEmailClick = async () => {
    if (onEmailClick) {
      onEmailClick();
      return;
    }

    setShowSignUp(true);
    setSignUpMethod("email");
  };

  const handleSignUpBack = () => {
    setShowSignUp(false);
    setSignUpMethod(null);
  };

  const handleSignUpSuccess = () => {
    // After successful sign-up, navigate to onboarding
    navigate("/onboarding");
  };

  const handleSignInClick = () => {
    setShowSignIn(true);
  };

  const handleBackClick = () => {
    setShowSignIn(false);
    setShowPasswordReset(false);
    setEmail("");
    setPassword("");
    setPhoneNumber("");
    setErrors({});
  };

  const handleForgotPasswordClick = () => {
    setShowPasswordReset(true);
  };

  const handlePasswordResetSuccess = () => {
    setShowPasswordReset(false);
    setShowSignIn(true);
    toast.success(t("entry.passwordResetSuccessSignIn"));
  };

  const handleSignInMethodChange = (method: "email" | "phone") => {
    setSignInMethod(method);
    localStorage.setItem("lastSignInMethod", method);
    setErrors({});
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!email.trim()) {
      setErrors({ email: "Email is required" });
      return;
    }

    if (!password) {
      setErrors({ password: t("entry.passwordRequired") });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await signIn(email.trim(), password);
      if (error) {
        toast.error(error.message || t("entry.failedToSignIn"));
        setErrors({ email: error.message });
      } else {
        toast.success(t("entry.welcomeBack"));
        // Navigation happens automatically via AuthContext
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("entry.somethingWentWrong"));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!selectedCountry) {
      setErrors({ phone: t("entry.pleaseSelectCountry") });
      return;
    }

    // Validate phone number and get E.164 format
    const validation = validatePhoneNumber(phoneNumber, selectedCountry.iso2);
    if (!validation.valid || !validation.e164) {
      setErrors({ phone: validation.error || t("entry.pleaseEnterValidPhoneNumber") });
      return;
    }

    // Use validated E.164 format
    const fullPhone = validation.e164;
    setIsLoading(true);
    setErrors({});

    try {
      // Send OTP for sign-in
      const { error: otpError } = await supabase.auth.signInWithOtp({
        phone: fullPhone,
        options: {
          shouldCreateUser: false, // Don't create user, just sign in
        },
      });

      if (otpError) {
        // Check if user doesn't exist
        if (
          otpError.message.includes("not found") || 
          otpError.message.includes("does not exist") ||
          otpError.message.includes("User not found")
        ) {
          setErrors({ phone: t("entry.noAccountFound") });
        } else if (
          otpError.message.includes("Unsupported phone provider") ||
          otpError.message.includes("phone provider") ||
          otpError.message.includes("SMS provider")
        ) {
          setErrors({ phone: t("entry.unsupportedPhoneProvider") });
        } else {
          // More user-friendly error messages
          const errorMessage = otpError.message || t("entry.failedToSendCode");
          setErrors({ phone: errorMessage });
        }
        setIsLoading(false);
        return;
      }

      setShowPhoneOTP(true);
      toast.success(t("otp.codeSentToIdentifier") + " " + fullPhone);
    } catch (err: any) {
      console.error("Error sending OTP:", err);
      setErrors({ phone: err.message || t("entry.failedToSendCode") });
      setIsLoading(false);
    }
  };

  const handlePhoneOTPVerify = async (otp: string): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!selectedCountry || !phoneNumber.trim()) {
        return { success: false, error: "Phone number is required" };
      }

      // Re-validate and get E.164 format for verification
      const validation = validatePhoneNumber(phoneNumber, selectedCountry.iso2);
      if (!validation.valid || !validation.e164) {
        return { success: false, error: "Invalid phone number format" };
      }
      
      const { data, error } = await supabase.auth.verifyOtp({
        phone: validation.e164,
        token: otp,
        type: "sms",
      });

      if (error) {
        return { success: false, error: error.message || t("otp.invalidCode") };
      }

      if (data.user) {
        return { success: true };
      }

      return { success: false, error: t("entry.verificationFailed") };
    } catch (err: any) {
      return { success: false, error: err.message || t("entry.verificationFailed") };
    }
  };

  const handlePhoneOTPResend = async (): Promise<void> => {
    await handlePhoneSignIn({ preventDefault: () => {} } as React.FormEvent);
  };

  const handlePhoneOTPSuccess = () => {
    // Navigate to pop balloon screen after successful sign-in
    navigate("/pop-balloon");
  };

  const handleCountryChange = (country: GeoCountry, code: string) => {
    setSelectedCountry(country);
    setCountryCode(code);
    // Clear any existing error when country changes
    setErrors({ ...errors, phone: undefined });
  };

  const validatePhoneNumber = (phone: string, countryIso2: string): { valid: boolean; error?: string; e164?: string } => {
    const digits = phone.replace(/\D/g, "");
    
    if (!digits || digits.length === 0) {
      return { valid: false, error: t("entry.pleaseEnterPhoneNumber") };
    }
    
    try {
      // Use libphonenumber-js to validate and format
      const phoneNumber = parsePhoneNumber(digits, countryIso2.toUpperCase() as any);
      
      if (!phoneNumber || !phoneNumber.isValid()) {
        return { valid: false, error: t("entry.pleaseEnterValidPhoneNumber") };
      }
      
      // Return E.164 format
      return { valid: true, e164: phoneNumber.format("E.164") };
    } catch (error) {
      // If parsing fails, try with country code prefix
      try {
        const fullNumber = `${countryCode}${digits}`;
        const phoneNumber = parsePhoneNumber(fullNumber);
        
        if (!phoneNumber || !phoneNumber.isValid()) {
          return { valid: false, error: t("entry.pleaseEnterValidPhoneNumber") };
        }
        
        return { valid: true, e164: phoneNumber.format("E.164") };
      } catch (parseError) {
        return { valid: false, error: t("entry.pleaseEnterValidPhoneNumber") };
      }
    }
  };


  return (
    <div className="min-h-screen gradient-soft relative overflow-hidden">
      {/* Decorative blur circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[hsl(var(--romantic-primary))]/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-[hsl(var(--romantic-accent))]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[hsl(var(--romantic-primary))]/5 rounded-full blur-3xl" />
      </div>

      {/* Language Switcher */}
      <LanguageSwitcher position="top-right" />

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12 safe-area-pt safe-area-pb">
        {/* Back Button - Only show in sign-in mode */}
        {showSignIn && (
          <button
            onClick={handleBackClick}
            className="absolute top-4 left-4 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors z-20"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t("entry.back")}</span>
          </button>
        )}

        {/* Logo - Always visible */}
        <div className="mb-0 fade-up">
          <div className="relative w-32 h-32 mx-auto mb-1 gradient-soft rounded-2xl p-1.5">
            <img 
              src="/new-logo.png" 
              alt="SpikeBloom Logo" 
              className="w-full h-full object-contain"
              style={{ mixBlendMode: 'multiply' }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = document.getElementById('logo-fallback');
                if (fallback) {
                  fallback.style.display = 'flex';
                }
              }}
            />
            <div 
              id="logo-fallback"
              className="absolute inset-0 w-full h-full rounded-2xl bg-gradient-to-br from-orange-400/20 to-orange-500/20 flex items-center justify-center border-2 border-orange-400/30 shadow-lg overflow-hidden" 
              style={{ display: 'none' }}
            >
              <Heart className="w-16 h-16 text-orange-500" />
            </div>
          </div>
        </div>

        {/* App Title */}
        <h1 className="text-4xl font-bold text-foreground mb-2 fade-up stagger-1" style={{ fontFamily: "'Bolder Elephant', cursive" }}>
          {t("app.name")}
        </h1>

        {/* Tagline */}
        <p className="text-lg text-[hsl(var(--romantic-muted-foreground))] mb-8 fade-up stagger-2">
          {t("app.tagline")}
        </p>

        {/* Content Area - Welcome, Sign Up, or Sign In */}
        <div className="w-full max-w-md">
          {showSignUp && signUpMethod ? (
            /* Sign Up Screen */
            <div className="bg-card rounded-3xl shadow-balloon p-8 animate-fade-in">
              {signUpMethod === "phone" ? (
                <PhoneSignUp onBack={handleSignUpBack} onSuccess={handleSignUpSuccess} />
              ) : (
                <EmailSignUp onBack={handleSignUpBack} onSuccess={handleSignUpSuccess} />
              )}
            </div>
          ) : showPasswordReset ? (
            <PasswordReset
              onBack={handleBackClick}
              onSuccess={handlePasswordResetSuccess}
            />
          ) : !showSignIn ? (
            /* Welcome Screen */
            <div className="space-y-4 animate-fade-in">
              {/* Live Presence Indicator */}
              <div className="mb-8 fade-up stagger-3">
                <LivePresenceIndicator />
              </div>

              {/* Primary CTA: Google Sign-In */}
              <button
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading || isLoading}
                className={cn(
                  "w-full mb-3 px-6 py-4 rounded-xl",
                  "btn-romantic glow-pulse",
                  "flex items-center justify-center gap-3",
                  "text-lg font-semibold",
                  "fade-up stagger-4",
                  (isGoogleLoading || isLoading) && "opacity-50 cursor-not-allowed"
                )}
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <GoogleIcon className="w-5 h-5" />
                )}
                {t("entry.continueWithGoogle")}
              </button>

              {/* Secondary CTA: Email */}
              <button
                onClick={handleEmailClick}
                disabled={isLoading}
                className={cn(
                  "w-full mb-6 px-6 py-4 rounded-xl",
                  "btn-romantic-outline",
                  "flex items-center justify-center gap-3",
                  "text-lg font-semibold",
                  "fade-up stagger-5",
                  isLoading && "opacity-50 cursor-not-allowed"
                )}
              >
                <Mail className="w-5 h-5" />
                {t("entry.continueWithEmail")}
              </button>

              {/* Sign-in link */}
              <div className="fade-up stagger-6">
                <p className="text-sm text-[hsl(var(--romantic-muted-foreground))] text-center">
                  {t("entry.alreadyHaveAccount")}{" "}
                  <button
                    onClick={handleSignInClick}
                    className="text-[hsl(var(--romantic-primary))] font-semibold hover:underline"
                  >
                    {t("entry.signIn")}
                  </button>
                </p>
              </div>
            </div>
          ) : (
            /* Sign In Panel */
            <div className="bg-card rounded-3xl shadow-balloon p-8 animate-fade-in">
              {/* Email Sign In Form */}
              <form onSubmit={handleEmailSignIn} className="space-y-4 animate-fade-in">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors({ ...errors, email: undefined });
                    }}
                    placeholder={t("entry.emailAddress")}
                    disabled={isLoading}
                    className={cn(
                      "w-full pl-12 pr-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] outline-none transition-all",
                      errors.email && "ring-2 ring-destructive",
                      isLoading && "opacity-50 cursor-not-allowed"
                    )}
                    required
                    aria-label="Email address"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-signin-error" : undefined}
                  />
                  {errors.email && (
                    <p id="email-signin-error" className="text-xs text-destructive mt-1 ml-1" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrors({ ...errors, password: undefined });
                    }}
                    placeholder={t("entry.password")}
                    disabled={isLoading}
                    className={cn(
                      "w-full pl-12 pr-12 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] outline-none transition-all",
                      errors.password && "ring-2 ring-destructive",
                      isLoading && "opacity-50 cursor-not-allowed"
                    )}
                    required
                    aria-label="Password"
                    aria-invalid={!!errors.password}
                    aria-describedby={errors.password ? "password-signin-error" : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  {errors.password && (
                    <p id="password-signin-error" className="text-xs text-destructive mt-1 ml-1" role="alert">
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Forgot Password Link */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleForgotPasswordClick}
                    className="text-sm text-[hsl(var(--romantic-primary))] hover:underline"
                  >
                    {t("entry.forgotPassword")}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={cn(
                    "w-full py-4 rounded-xl bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] text-white font-semibold",
                    "shadow-[0_10px_40px_-10px_hsl(var(--romantic-primary)/0.25%)]",
                    "hover:shadow-[0_15px_50px_-10px_hsl(var(--romantic-primary)/0.35%)]",
                    "hover:scale-[1.02] active:scale-[0.98] transition-all",
                    "disabled:opacity-50 flex items-center justify-center gap-2"
                  )}
                >
                  {isLoading && <Loader2 className="w-5 h-5 animate-spin" />}
                  {isLoading ? t("entry.signingIn") : t("entry.signIn")}
                </button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-muted"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t("entry.or")}</span>
                </div>
              </div>

              {/* Google Sign In Button */}
              <button
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading || isLoading}
                className={cn(
                  "w-full px-6 py-4 rounded-xl",
                  "btn-romantic-outline",
                  "flex items-center justify-center gap-3",
                  "text-lg font-semibold",
                  (isGoogleLoading || isLoading) && "opacity-50 cursor-not-allowed"
                )}
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <GoogleIcon className="w-5 h-5" />
                )}
                {t("entry.continueWithGoogle")}
              </button>

              {/* Form Fields - Phone Mode */}
              {FEATURE_FLAGS.ENABLE_PHONE_SIGNUP && signInMethod === "phone" && !showPhoneOTP && (
                <form onSubmit={handlePhoneSignIn} className="space-y-4 animate-fade-in">
                  {/* Phone Header */}
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-bold text-foreground mb-2">{t("entry.enterPhoneNumber")}</h2>
                    <p className="text-sm text-muted-foreground">
                      {t("entry.phoneVerificationMessage")}
                    </p>
                  </div>

                  {/* Unified Phone Input */}
                  <PhoneInput
                    value={phoneNumber}
                    onChange={(value) => {
                      setPhoneNumber(value);
                      setErrors({ ...errors, phone: undefined });
                    }}
                    onCountryChange={handleCountryChange}
                    selectedCountry={selectedCountry}
                    countryCode={countryCode}
                    error={errors.phone}
                    disabled={isLoading}
                          placeholder={t("entry.enterPhoneNumber")}
                    autoFocus
                  />

                  {/* Send Verification Code Button */}
                  <button
                    type="submit"
                    disabled={isLoading || !phoneNumber.trim() || !selectedCountry}
                    className={cn(
                      "w-full py-4 rounded-xl bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] text-white font-semibold",
                      "shadow-[0_10px_40px_-10px_hsl(var(--romantic-primary)/0.25%)]",
                      "hover:shadow-[0_15px_50px_-10px_hsl(var(--romantic-primary)/0.35%)]",
                      "hover:scale-[1.02] active:scale-[0.98] transition-all",
                      "disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    )}
                  >
                    {isLoading && <Loader2 className="w-5 h-5 animate-spin" />}
                    {isLoading ? t("entry.sending") : t("entry.sendVerificationCode")}
                  </button>

                  {/* Links */}
                  <div className="text-center text-sm">
                    <p className="text-muted-foreground">
                      {t("entry.preferEmail")}{" "}
                      <button
                        type="button"
                        onClick={() => handleSignInMethodChange("email")}
                        className="text-[hsl(var(--romantic-primary))] font-semibold hover:underline"
                      >
                        {t("entry.useEmailInstead")}
                      </button>
                    </p>
                  </div>
                </form>
              )}

              {/* Phone OTP Verification */}
              {FEATURE_FLAGS.ENABLE_PHONE_SIGNUP && signInMethod === "phone" && showPhoneOTP && (
                <OTPVerification
                  type="phone"
                  identifier={selectedCountry ? `${countryCode}${phoneNumber}` : phoneNumber}
                  onVerify={handlePhoneOTPVerify}
                  onResend={handlePhoneOTPResend}
                  onBack={() => {
                    setShowPhoneOTP(false);
                    setIsLoading(false);
                    setErrors({});
                  }}
                  onSuccess={handlePhoneOTPSuccess}
                />
              )}
            </div>
          )}
        </div>

        {/* Terms/Privacy footer */}
        <p className="absolute bottom-4 left-0 right-0 text-center text-[11px] text-[hsl(var(--romantic-muted-foreground))]/70 px-4">
          {t("entry.termsAndPrivacy")}{" "}
          <a href="/terms" className="underline hover:text-[hsl(var(--romantic-primary))]">
            {t("entry.termsOfService")}
          </a>{" "}
          {t("entry.and")}{" "}
          <a href="/privacy" className="underline hover:text-[hsl(var(--romantic-primary))]">
            {t("entry.privacyPolicy")}
          </a>
          .
        </p>
      </div>
    </div>
  );
};
